<script setup lang="ts">
import { ref } from 'vue';

const rootRef = ref<HTMLElement | null>(null);

const setItemState = (menuItem: HTMLElement, open: boolean) => {
  menuItem.classList.toggle('open', open);
  menuItem.classList.toggle('closed', !open);
};

const closeItemAndDescendants = (menuItem: HTMLElement) => {
  setItemState(menuItem, false);
  menuItem.querySelectorAll<HTMLElement>('.mobilemenu__item').forEach(el => {
    setItemState(el, false);
  });
};

const closeSiblings = (menuItem: HTMLElement) => {
  const parentList = menuItem.parentElement;
  if (!parentList?.classList.contains('mobilemenu__list')) {
    return;
  }

  parentList.querySelectorAll<HTMLElement>(':scope > .mobilemenu__item').forEach(sibling => {
    if (sibling !== menuItem) {
      closeItemAndDescendants(sibling);
    }
  });
};

const getMenuItem = (itemId: string): HTMLElement | null => {
  return (
    rootRef.value?.querySelector<HTMLElement>(`.mobilemenu__item[data-menu-item-id="${itemId}"]`) ??
    null
  );
};

const toggleItem = (itemId: string) => {
  const menuItem = getMenuItem(itemId);
  if (!menuItem) {
    return;
  }

  if (menuItem.classList.contains('open')) {
    closeItemAndDescendants(menuItem);
    return;
  }

  closeSiblings(menuItem);
  setItemState(menuItem, true);
};

const closeItem = (itemId: string) => {
  const menuItem = getMenuItem(itemId);
  if (menuItem) {
    closeItemAndDescendants(menuItem);
  }
};

const closeAllItems = () => {
  rootRef.value?.querySelectorAll<HTMLElement>('.mobilemenu__item').forEach(menuItem => {
    setItemState(menuItem, false);
  });
};

const isItemOpen = (itemId: string): boolean => {
  return getMenuItem(itemId)?.classList.contains('open') ?? false;
};

const handleClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  const toggleButton = target.closest('[data-mobile-menu-toggle]');

  if (!toggleButton) return;

  const menuItem = toggleButton.closest('.mobilemenu__item');
  if (!menuItem) return;

  const itemId = menuItem.getAttribute('data-menu-item-id');
  if (!itemId) return;

  event.preventDefault();
  event.stopPropagation();
  toggleItem(itemId);
};

defineExpose({
  toggleItem,
  closeItem,
  closeAllItems,
  isItemOpen,
});
</script>

<template>
  <div ref="rootRef" class="mobilemenu" @click="handleClick">
    <slot />
  </div>
</template>

<style lang="scss"></style>
